import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  AppsCreateAppRequest,
  AppsPlatformCredentialResponse,
  AppsUpdateAppRequest,
  AppsUpsertPlatformCredentialRequest,
  AppsUpsertPlatformRequest,
} from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import { requireWorkspaceId } from '../shared/workspace';
import type { HookMutationConfig } from '../shared/react-query-types';
import { appKeys, type App, type AppPlatform } from './entity';

/**
 * Every write here can change what the list shows — a rename, the default flag
 * the server moves between apps, a platform gaining or losing its credential —
 * so each one reloads the list alongside the app it touched.
 */
function invalidateApp(qc: QueryClient, workspaceId: number, appId: number): void {
  void qc.invalidateQueries({ queryKey: appKeys.list(workspaceId) });
  void qc.invalidateQueries({ queryKey: appKeys.detail(workspaceId, appId) });
}

export interface CreateAppInput extends AppsCreateAppRequest {
  workspaceId: number;
}

/**
 * Registers an app. The workspace's first app becomes its default; the server
 * decides that, so the create body carries only the name.
 *
 * @public
 */
export async function createApp({ workspaceId, ...write }: CreateAppInput): Promise<App> {
  const data = await hydraUiApi.apps.create({
    params: { path: { environment_id: String(workspaceId) } },
    body: write,
  });
  return data.app;
}

type UseCreateAppVariables = Omit<CreateAppInput, 'workspaceId'>;

interface UseCreateAppOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<App, UseCreateAppVariables>;
}

/** @public */
export const useCreateApp = ({ workspaceId: overrideWorkspaceId, mutationConfig }: UseCreateAppOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseCreateAppVariables) => createApp({ workspaceId, ...variables }),
    onSuccess: (created, ...callbackArgs) => {
      invalidateApp(qc, workspaceId, created.id);
      onSuccess?.(created, ...callbackArgs);
    },
  });
};

export interface UpdateAppInput extends AppsUpdateAppRequest {
  workspaceId: number;
  appId: number;
}

/**
 * Renames an app or promotes it to the workspace default. A workspace holds
 * exactly one default, so promoting one demotes the previous one server-side —
 * which is why the hook reloads the whole list rather than just this app.
 *
 * @public
 */
export async function updateApp({ workspaceId, appId, ...write }: UpdateAppInput): Promise<App> {
  const data = await hydraUiApi.apps.update({
    params: { path: { environment_id: String(workspaceId), app_id: String(appId) } },
    body: write,
  });
  return data.app;
}

type UseUpdateAppVariables = Omit<UpdateAppInput, 'workspaceId'>;

interface UseUpdateAppOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<App, UseUpdateAppVariables>;
}

/** @public */
export const useUpdateApp = ({ workspaceId: overrideWorkspaceId, mutationConfig }: UseUpdateAppOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseUpdateAppVariables) => updateApp({ workspaceId, ...variables }),
    onSuccess: (updated, variables, ...callbackArgs) => {
      invalidateApp(qc, workspaceId, variables.appId);
      onSuccess?.(updated, variables, ...callbackArgs);
    },
  });
};

export interface DeleteAppInput {
  workspaceId: number;
  appId: number;
}

/** @public */
export async function deleteApp({ workspaceId, appId }: DeleteAppInput): Promise<void> {
  await hydraUiApi.apps.delete({
    params: { path: { environment_id: String(workspaceId), app_id: String(appId) } },
  });
}

type UseDeleteAppVariables = Omit<DeleteAppInput, 'workspaceId'>;

interface UseDeleteAppOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<void, UseDeleteAppVariables>;
}

/** @public */
export const useDeleteApp = ({ workspaceId: overrideWorkspaceId, mutationConfig }: UseDeleteAppOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseDeleteAppVariables) => deleteApp({ workspaceId, ...variables }),
    onSuccess: (result, variables, ...callbackArgs) => {
      void qc.invalidateQueries({ queryKey: appKeys.list(workspaceId) });
      qc.removeQueries({ queryKey: appKeys.detail(workspaceId, variables.appId) });
      qc.removeQueries({ queryKey: appKeys.usage(workspaceId, variables.appId) });
      onSuccess?.(result, variables, ...callbackArgs);
    },
  });
};

export interface LinkAppPlatformInput extends AppsUpsertPlatformRequest {
  workspaceId: number;
  appId: number;
}

/**
 * Registers a platform on an app, optionally pointing it at a credential that
 * already exists. This is how a second platform reuses the key another platform
 * uploaded, instead of uploading the same file twice.
 *
 * @public
 */
export async function linkAppPlatform({ workspaceId, appId, ...write }: LinkAppPlatformInput): Promise<AppPlatform> {
  const data = await hydraUiApi.apps.updatePlatforms({
    params: { path: { environment_id: String(workspaceId), app_id: String(appId) } },
    body: write,
  });
  return data.platform;
}

type UseLinkAppPlatformVariables = Omit<LinkAppPlatformInput, 'workspaceId'>;

interface UseLinkAppPlatformOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<AppPlatform, UseLinkAppPlatformVariables>;
}

/** @public */
export const useLinkAppPlatform = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UseLinkAppPlatformOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseLinkAppPlatformVariables) => linkAppPlatform({ workspaceId, ...variables }),
    onSuccess: (linked, variables, ...callbackArgs) => {
      invalidateApp(qc, workspaceId, variables.appId);
      onSuccess?.(linked, variables, ...callbackArgs);
    },
  });
};

export interface UpsertAppPlatformCredentialInput extends AppsUpsertPlatformCredentialRequest {
  workspaceId: number;
  appId: number;
  platform: string;
}

/**
 * Uploads a credential and attaches it to one of an app's platforms in a single
 * call. The reply carries the stored platform, whose `credential_id` is what a
 * second platform links to when both send through the same key.
 *
 * @public
 */
export async function upsertAppPlatformCredential({
  workspaceId,
  appId,
  platform,
  ...write
}: UpsertAppPlatformCredentialInput): Promise<AppsPlatformCredentialResponse> {
  return hydraUiApi.apps.updatePlatformsCredential({
    params: { path: { environment_id: String(workspaceId), app_id: String(appId), platform } },
    body: write,
  });
}

type UseUpsertAppPlatformCredentialVariables = Omit<UpsertAppPlatformCredentialInput, 'workspaceId'>;

interface UseUpsertAppPlatformCredentialOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<AppsPlatformCredentialResponse, UseUpsertAppPlatformCredentialVariables>;
}

/** @public */
export const useUpsertAppPlatformCredential = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UseUpsertAppPlatformCredentialOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseUpsertAppPlatformCredentialVariables) =>
      upsertAppPlatformCredential({ workspaceId, ...variables }),
    onSuccess: (saved, variables, ...callbackArgs) => {
      invalidateApp(qc, workspaceId, variables.appId);
      onSuccess?.(saved, variables, ...callbackArgs);
    },
  });
};

export interface DeleteAppPlatformInput {
  workspaceId: number;
  appId: number;
  platform: string;
}

/**
 * Removes a platform from an app, along with its credential link. Used when a
 * platform is switched off during an edit.
 *
 * @public
 */
export async function deleteAppPlatform({ workspaceId, appId, platform }: DeleteAppPlatformInput): Promise<void> {
  await hydraUiApi.apps.deletePlatforms({
    params: { path: { environment_id: String(workspaceId), app_id: String(appId), platform } },
  });
}

type UseDeleteAppPlatformVariables = Omit<DeleteAppPlatformInput, 'workspaceId'>;

interface UseDeleteAppPlatformOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<void, UseDeleteAppPlatformVariables>;
}

/** @public */
export const useDeleteAppPlatform = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UseDeleteAppPlatformOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseDeleteAppPlatformVariables) => deleteAppPlatform({ workspaceId, ...variables }),
    onSuccess: (result, variables, ...callbackArgs) => {
      invalidateApp(qc, workspaceId, variables.appId);
      onSuccess?.(result, variables, ...callbackArgs);
    },
  });
};
